import {
  FORM_QUESTION_ANSWERED,
  FORM_QUESTION_NOT_ANSWERED,
  FORM_QUESTION_ERRORED,
  RESET_QUESTIONNAIRE,
  FORM_QUESTIONNAIRE_OPENED,
  FORM_SUBMITTED,
  SUPPORTING_DOC_DELETE_SUCCESS,
  SUPPORTING_DOC_ADDED
} from 'actions';

import {
  w9Questions,
  w8benQuestions,
  crsiQuestions,
  crseQuestions,
  crscpQuestions
} from 'common/questionnaires/formsQuestionnaire/questionsData';

import { DynamicUpdateAction, DynamicUpdateCondition } from 'common/questionnaires/types';

const findAndUpdateQuestion = (questions, action) => {
  if (!action || !questions || !Array.isArray(questions) || (Array.isArray(questions) && questions.length === 0)) {
    return [];
  }

  const updatedQuestions = questions.map((q) => {
    if (q.id === action.id) {
      q = {
        ...q,
        isAnswered: action.isAnswered,
        hasError: action.hasError,
        errorMessage: action.customError ? action.customError : q.errorMessage,
        answer: action.answer };
    }
    return q;
  });

  return updatedQuestions;
};

const checkForDynamicUpdates = questions => questions.map((q) => {
  const updatedQuestion = { ...q };

  if (q.dynamicUpdate && q.dynamicUpdate.inputQuestionId && q.dynamicUpdate.expectedValue !== null) {
    const inputQuestion = questions.find(question => question.id === q.dynamicUpdate.inputQuestionId);

    if (inputQuestion !== null && q.dynamicUpdate.condition === DynamicUpdateCondition.IsNot) {
      updatedQuestion.dynamicUpdate = {
        ...updatedQuestion.dynamicUpdate,
        conditionMet: (inputQuestion.answer === null || inputQuestion.answer !== q.dynamicUpdate.expectedValue)
      };
    }

    if (inputQuestion !== null && q.dynamicUpdate.condition === DynamicUpdateCondition.Is) {
      updatedQuestion.dynamicUpdate = {
        ...updatedQuestion.dynamicUpdate,
        conditionMet: (inputQuestion.answer !== null && inputQuestion.answer === q.dynamicUpdate.expectedValue)
      };
    }

    if (updatedQuestion !== null && updatedQuestion.dynamicUpdate.conditionMet
       && (updatedQuestion.dynamicUpdate.action === DynamicUpdateAction.HideAndClear || updatedQuestion.dynamicUpdate.action === DynamicUpdateAction.DisableAndClear)) {
      updatedQuestion.answer = null;
      updatedQuestion.isAnswered = false;
      updatedQuestion.hasError = false;
    }
  }

  return updatedQuestion;
});

const checkPartialConstraints = (questions, answers, runValidation) => {
  for (let i = 0; i < questions.length - 1; i += 1) {
    if (questions[i].partialFieldConstraint) {
      let needsToAnswer = false;
      let allConstraintsValid = true;
      questions[i].partialFieldConstraint.forEach((item) => {
        const constrainedField = questions.find(q => q.id === item);
        if ((constrainedField.answer !== undefined && constrainedField.answer.length > 0) && (questions[i].answer === undefined || questions[i].answer.length <= 0)) {
          needsToAnswer = true;
          allConstraintsValid = false;
        } else {
          needsToAnswer = false;
        }

        if (runValidation) {
          questions[i].hasError = needsToAnswer || !allConstraintsValid;
        }
      });
    }
  }

  return questions;
};

const checkCapacityQuestionConstraints = (questions, answers, runValidation) => {
  const capacityDropdown = questions.find(q => q.complexCapacityContraint);

  const items = [];
  capacityDropdown.complexCapacityContraint.givenNameId.forEach((element) => {
    const item = questions.find(q => q.id === element);
    if (item) {
      items.push(item.answer);
    }
  });

  if (items.length !== capacityDropdown.complexCapacityContraint.givenNameId.length) {
    return questions;
  }

  const signerName = questions.find(q => q.id === capacityDropdown.complexCapacityContraint.signerNameId);

  if (!capacityDropdown || !signerName) {
    return questions;
  }

  const fullName = items.join('');

  return questions.map((q) => {
    if (q.id === capacityDropdown.id) {
      const signerNameLower = answers[signerName.name] ? answers[signerName.name].toString().toLowerCase().split(' ').join('') : '';
      const givenNameLower = fullName.toString().toLowerCase().split(' ').join('');
      const namesMatch = signerNameLower === givenNameLower;
      const capacityDropDownMatches = capacityDropdown.answer === capacityDropdown.complexCapacityContraint.expectedValue;

      return { ...q, hasError: (!namesMatch && capacityDropDownMatches && runValidation) };
    }

    return { ...q };
  });
};

const questionnaireDefaultState = {
  runValidation: false,
  showValidationErrors: false,
  questions: [],
  answers: {},
  formErrors: [],
  validationResult: null,
  allFieldsValidated: false,
};

function formQuestionnaire(state = { ...questionnaireDefaultState }, action) {
  switch (action.type) {
    case FORM_SUBMITTED:
      return {
        ...state,
        runValidation: true,
        allFieldsValidated: true
      };
    case FORM_QUESTIONNAIRE_OPENED:
      switch (action.formType) {
        case 'w9_2018':
          return { ...questionnaireDefaultState,
            questions: Object.assign([], w9Questions),
            answers: { ...state.answers, id: action.formId, subjectToBackupWhd: false }
          };
        case 'w8ben':
          return { ...questionnaireDefaultState,
            questions: Object.assign([], w8benQuestions),
            answers: { ...state.answers, id: action.formId }
          };
        case 'w8ben_2021':
          return { ...questionnaireDefaultState,
            questions: Object.assign([], w8benQuestions),
            answers: { ...state.answers, id: action.formId }
          };
        case 'crsi':
          return { ...questionnaireDefaultState,
            questions: Object.assign([], crsiQuestions),
            answers: { ...state.answers, id: action.formId }
          };
        case 'crse':
          return { ...questionnaireDefaultState,
            questions: Object.assign([], crseQuestions),
            answers: { ...state.answers, id: action.formId }
          };
        case 'crscp':
          return { ...questionnaireDefaultState,
            questions: Object.assign([], crscpQuestions),
            answers: { ...state.answers, id: action.formId }
          };
        default:
          return { ...questionnaireDefaultState, formErrors: [] };
      }
    case RESET_QUESTIONNAIRE:
      return { ...questionnaireDefaultState };
    case FORM_QUESTION_ANSWERED: {
      let updatedState = {
        ...state,
        answers: { ...state.answers, [action.name]: action.answer },
        questions: findAndUpdateQuestion(state.questions, action),
      };

      if ((action.answer !== state.answers[action.name])) {
        updatedState.allFieldsValidated = false;
      }

      if (updatedState.formErrors.indexOf(action.id) > -1) {
        updatedState.formErrors = updatedState.formErrors.filter(x => x !== action.id);
      }

      if (updatedState && updatedState.questions && action && action.id) {
        const questionCausesDynamicUpdate = updatedState.questions.filter(q => q.dynamicUpdate && q.dynamicUpdate.inputQuestionId === action.id).length !== 0;
        if (questionCausesDynamicUpdate) {
          updatedState = { ...updatedState, questions: checkForDynamicUpdates(updatedState.questions) };
          updatedState = { ...updatedState, questions: checkForDynamicUpdates(updatedState.questions) };
          updatedState = { ...updatedState, questions: checkForDynamicUpdates(updatedState.questions) };
        }

        if (updatedState.questions.filter(q => q.partialFieldConstraint && q.partialFieldConstraint.length && q.partialFieldConstraint.length > 0).length !== 0) {
          updatedState = { ...updatedState, questions: checkPartialConstraints(updatedState.questions, updatedState.answers, updatedState.runValidation) };
        }

        if (updatedState.questions.filter(q => q.complexCapacityContraint && q.complexCapacityContraint.signerNameId && q.complexCapacityContraint.givenNameId).length !== 0) {
          updatedState = { ...updatedState, questions: checkCapacityQuestionConstraints(updatedState.questions, updatedState.answers, updatedState.runValidation) };
        }

        const errored = updatedState.questions.filter(q => q.hasError);

        if (errored.length > 0) {
          errored.forEach((error) => {
            if (updatedState.formErrors.indexOf(error.id) < 0) {
              return errored.map(item => updatedState.formErrors.push(item.id));
            }
            return error;
          });
        }

        if (errored.length === 0) {
          updatedState.formErrors = [];
        }
      }

      updatedState.formErrors = [...new Set(updatedState.formErrors)];

      return updatedState;
    }
    case FORM_QUESTION_NOT_ANSWERED:
    case FORM_QUESTION_ERRORED: {
      let updatedState = {
        ...state,
        questions: findAndUpdateQuestion(state.questions, action),
      };

      if (updatedState.runValidation && updatedState.formErrors.indexOf(action.id) < 0) {
        updatedState.formErrors.push(action.id);
      }

      if (action.hasError) {
        updatedState = { ...updatedState, allFieldsValidated: false };
      }

      if (updatedState && updatedState.questions && action && action.id) {
        const questionCausesDynamicUpdate = updatedState.questions.filter(q => q.dynamicUpdate && q.dynamicUpdate.inputQuestionId === action.id).length !== 0;
        if (questionCausesDynamicUpdate) {
          updatedState = { ...updatedState, questions: checkForDynamicUpdates(updatedState.questions) };
        }

        if (updatedState.questions.filter(q => q.complexCapacityContraint && q.complexCapacityContraint.signerNameId && q.complexCapacityContraint.givenNameId).length !== 0) {
          updatedState = { ...updatedState, questions: checkCapacityQuestionConstraints(updatedState.questions, updatedState.runValidation) };
        }
      }
      return updatedState;
    }
    case SUPPORTING_DOC_DELETE_SUCCESS:
    case SUPPORTING_DOC_ADDED:
      return {
        ...state,
        allFieldsValidated: false
      };
    default:
      return state;
  }
}

export default formQuestionnaire;
